import React, { useState } from 'react';
import { KidsCourse, KidsCourseAgeGroup, KidsCourseTag } from 'types';
import {
    MenuItemBlock,
    MenuTitle,
    MenuListItem,
    InnerCheckBox,
    BodyContent,
    WhiteHeader,
    ContentBlock,
    HideBlock,
    SearchBlock,
    ListEmpty,
    TitleBlock,
    ListBlockCourses,
    MenuList,
    MenuBlock,
    Content,
    Title,
    ListBlockProfessions,
    MoreButton,
    ChangeFilters,
} from 'containers/pages/courses-list/components/body/styled';
import { CentralBlock } from 'containers/pages/courses-list/components/styled';
import { CourseItem } from '../course-item';
import { ProfessionItem } from '../profession-item';
import { useTranslation } from 'react-i18next';

interface FilterItem {
    id: string | number;
    label: string;
    checked: boolean;
    onChange: () => void;
}

interface FilterSectionProps {
    title: string;
    items: FilterItem[];
}

const FilterSection: React.FC<FilterSectionProps> = ({ title, items }) => (
    <MenuItemBlock>
        <MenuTitle>{title}</MenuTitle>
        <MenuList>
            {items.map((item) => (
                <MenuListItem key={item.id}>
                    <InnerCheckBox
                        value={item.checked}
                        onChange={item.onChange}
                        size={'medium'}>
                        <span>{item.label}</span>
                    </InnerCheckBox>
                </MenuListItem>
            ))}
        </MenuList>
    </MenuItemBlock>
);

interface KidsCoursesContentProps {
    projects: KidsCourse[];
    materials: KidsCourse[];
    hasMoreProjects: boolean;
    hasMoreMaterials: boolean;
    isProjectsLoading: boolean;
    isMaterialsLoading: boolean;

    checkedInterests: { projects: boolean; materials: boolean };
    selectedAges: KidsCourseAgeGroup[];

    shouldShowProjects: boolean;
    shouldShowMaterials: boolean;

    projectsTotal: number;
    materialsTotal: number;

    onAgeToggle: (age: KidsCourseAgeGroup) => void;
    onInterestToggle: (type: 'projects' | 'materials') => void;
    onLoadMoreProjects: () => void;
    onLoadMoreMaterials: () => void;
}

export const KidsCoursesContent: React.FC<KidsCoursesContentProps> = ({
    projects,
    materials,
    hasMoreProjects,
    hasMoreMaterials,
    isProjectsLoading,
    isMaterialsLoading,
    checkedInterests,
    selectedAges,
    shouldShowProjects,
    shouldShowMaterials,
    onAgeToggle,
    onInterestToggle,
    onLoadMoreProjects,
    onLoadMoreMaterials,
    projectsTotal,
    materialsTotal,
}) => {
    const { t } = useTranslation(['common/shared', 'pages/kids-courses']);

    const [showFilters] = useState(true);

    const isProjectsEmpty = !shouldShowProjects || projects.length === 0;
    const isMaterialsEmpty = !shouldShowMaterials || materials.length === 0;

    const isListEmpty = isProjectsEmpty && isMaterialsEmpty;

    const interestFilters: FilterItem[] = [
        KidsCourseTag.Projects,
        KidsCourseTag.LearningMaterials,
    ].map((tag) => {
        const type = tag === KidsCourseTag.Projects ? 'projects' : 'materials';
        return {
            id: tag,
            label: t(`pages/kids-courses:interest-${tag}`),
            checked: checkedInterests[type],
            onChange: () => onInterestToggle(type),
        };
    });

    const ageFilters: FilterItem[] = Object.values(KidsCourseAgeGroup)
        .filter((v): v is KidsCourseAgeGroup => typeof v === 'number')
        .map((age) => ({
            id: age,
            label: t(`pages/kids-courses:age-group-${age}`),
            checked: selectedAges.includes(age),
            onChange: () => onAgeToggle(age),
        }));

    return (
        <BodyContent styles={{ marginBottom: '60px;' }}>
            <WhiteHeader />
            <Content>
                <CentralBlock>
                    <ContentBlock>
                        <MenuBlock>
                            <HideBlock hide={!showFilters}>
                                <FilterSection
                                    title={t('pages/kids-courses:interests')}
                                    items={interestFilters}
                                />
                                <FilterSection
                                    title={t('pages/kids-courses:age-group')}
                                    items={ageFilters}
                                />
                            </HideBlock>
                        </MenuBlock>

                        <SearchBlock>
                            {isListEmpty && (
                                <>
                                    <ListEmpty>
                                        {t('common/shared:list-empty')}
                                    </ListEmpty>
                                    <ChangeFilters>
                                        {t('common/shared:change-filters')}
                                    </ChangeFilters>
                                </>
                            )}

                            {shouldShowProjects && projects.length > 0 && (
                                <>
                                    <TitleBlock>
                                        <Title>
                                            {t('pages/kids-courses:projects')}
                                            <span>{projectsTotal}</span>
                                        </Title>
                                    </TitleBlock>

                                    <ListBlockProfessions>
                                        {projects.map((project) => (
                                            <ProfessionItem
                                                key={project.id}
                                                course={project}
                                            />
                                        ))}
                                    </ListBlockProfessions>

                                    {hasMoreProjects && (
                                        <MoreButton
                                            onClick={onLoadMoreProjects}
                                            disabled={isProjectsLoading}>
                                            <span>
                                                {t(
                                                    'pages/kids-courses:show-more',
                                                    {
                                                        count:
                                                            projectsTotal -
                                                            projects.length,
                                                    }
                                                )}
                                            </span>
                                        </MoreButton>
                                    )}
                                </>
                            )}

                            {shouldShowMaterials && materials.length > 0 && (
                                <>
                                    <TitleBlock style={{ marginTop: '40px' }}>
                                        <Title>
                                            {t(
                                                'pages/kids-courses:learning-materials'
                                            )}
                                            <span>{materialsTotal}</span>
                                        </Title>
                                    </TitleBlock>

                                    <ListBlockCourses>
                                        {materials.map((material) => (
                                            <CourseItem
                                                key={material.id}
                                                course={material}
                                            />
                                        ))}
                                    </ListBlockCourses>

                                    {hasMoreMaterials && (
                                        <MoreButton
                                            onClick={onLoadMoreMaterials}
                                            disabled={isMaterialsLoading}>
                                            {t('pages/kids-courses:show-more', {
                                                count:
                                                    materialsTotal -
                                                    materials.length,
                                            })}
                                        </MoreButton>
                                    )}
                                </>
                            )}
                        </SearchBlock>
                    </ContentBlock>
                </CentralBlock>
            </Content>
        </BodyContent>
    );
};
